/**
 * @module ui/dom/dom-manager
 * @description Centralized DOM element reference management
 * Provides safe access to DOM elements with existence checks
 */

import { logger } from '../../utils/logger.js';

// DOM element references (cached after initialization)
let elements = {};

/**
 * Initialize DOM element references
 * Should be called after DOM is fully loaded
 * @returns {boolean} True if all critical elements found, false otherwise
 */
export function initializeDOMElements() {
    elements = {
        // Authentication sections
        signInSection: document.getElementById('sign-in-section'),
        loadingSection: document.getElementById('loading-section'),
        projectSelectionSection: document.getElementById('project-selection-section'),
        filesSection: document.getElementById('files-section'),

        // Header elements
        siteTitle: document.getElementById('site-title'),

        // User info
        userInfo: document.getElementById('user-info'),
        userEmail: document.getElementById('user-email'),

        // Auth buttons
        signInBtn: document.getElementById('sign-in-btn'),
        signOutBtn: document.getElementById('sign-out-btn'),

        // Content containers
        filesContainer: document.getElementById('files-container'),
        fileMetadata: document.getElementById('file-metadata'),

        // Error display
        errorMessage: document.getElementById('error-message'),

        // Project/Entity selection
        projectSelector: document.getElementById('project-selector'),
        projectError: document.getElementById('project-error')
    };

    // Check for critical elements
    const criticalElements = [
        'signInSection',
        'loadingSection',
        'filesSection',
        'filesContainer'
    ];

    const missingElements = criticalElements.filter(key => !elements[key]);

    if (missingElements.length > 0) {
        logger.warn('Missing critical DOM elements:', missingElements);
        return false;
    }

    return true;
}

/**
 * Get a DOM element by key with existence check
 * @param {string} key - Element key from initialized elements
 * @returns {HTMLElement|null} The DOM element or null if not found
 */
export function getElement(key) {
    if (!elements[key]) {
        logger.warn(`DOM element "${key}" not found or not initialized`);
        return null;
    }
    return elements[key];
}

/**
 * Check if a DOM element exists
 * @param {string} key - Element key to check
 * @returns {boolean} True if element exists
 */
export function hasElement(key) {
    return !!elements[key];
}

/**
 * Get all initialized DOM elements
 * @returns {Object} Object containing all DOM element references
 */
export function getAllElements() {
    return { ...elements };
}

/**
 * Update a specific element reference (useful for dynamically created elements)
 * @param {string} key - Element key
 * @param {HTMLElement} element - DOM element
 */
export function updateElement(key, element) {
    elements[key] = element;
}

// Export individual element getters for convenience
export const getDOMElements = {
    get signInSection() { return getElement('signInSection'); },
    get loadingSection() { return getElement('loadingSection'); },
    get projectSelectionSection() { return getElement('projectSelectionSection'); },
    get filesSection() { return getElement('filesSection'); },
    get siteTitle() { return getElement('siteTitle'); },
    get userInfo() { return getElement('userInfo'); },
    get userEmail() { return getElement('userEmail'); },
    get signInBtn() { return getElement('signInBtn'); },
    get signOutBtn() { return getElement('signOutBtn'); },
    get filesContainer() { return getElement('filesContainer'); },
    get fileMetadata() { return getElement('fileMetadata'); },
    get errorMessage() { return getElement('errorMessage'); },
    get projectSelector() { return getElement('projectSelector'); },
    get projectError() { return getElement('projectError'); }
};

export default {
    initializeDOMElements,
    getElement,
    hasElement,
    getAllElements,
    updateElement,
    getDOMElements
};
